export const drawOverviewArticle = {
    id: 'draw-overview',
    title: 'Общие принципы рисования',
    category: 'Режим рисования',
    requiredModule: null,
    sandboxType: 'drawing-plot',
    summary: 'Четыре инструмента рисования, 4 шага создания объекта, чертежный стиль, клавиша Shift и изменение границ.',
    markdown: `
> **Режим рисования** — это главный рабочий инструмент администратора. С его помощью вы легко нанесёте на карту границы земельных участков, контуры домов, дороги, заборы и точечные метки без сложных инженерных программ.

---

## 1. Четыре инструмента для любых объектов

В верхней панели инструментов доступны 4 кнопки черчения. Каждый инструмент сам знает, как рассчитывать размеры:

:::grid cols=2
:::card icon="square" title="1. Земельные участки (Полигоны)"
Для очерчивания границ земли, скверов, очередей застройки и полей.  
*Система сама на лету вычисляет точную площадь в квадратных метрах и сотках.*
:::

:::card icon="box" title="2. Здания и дома (3D-объёмы)"
Для фундаментов, коттеджей, таунхаусов и хозпостроек.  
*Дома сразу получают высоту и автоматически поднимаются в объемном 3D-виде.*
:::

:::card icon="activity" title="3. Дороги и линии"
Для улиц, проездов, заборов, линий электропередач или труб водопровода.  
*Система автоматически считает и показывает общую длину трассы в метрах и километрах.*
:::

:::card icon="map-pin" title="4. Метки и ориентиры (Точки)"
Для точечных объектов: шлагбаумов, КПП, детских площадок, причалов, фонарей и парковок.  
*Каждой точке можно в один клик привязать красивую цветную иконку из каталога.*
:::
:::

---

## 2. Как создаётся объект: 4 простых шага

Вам не нужно вручную открывать базы данных или вбивать координаты — процесс полностью автоматизирован:

| Шаг | Что делаете вы | Что делает система |
| :---: | :--- | :--- |
| **1** | Нажимаете на верхней панели нужную кнопку (например, **«Участки»** или **«Здания»**). | Карта переходит в режим черчения: курсор превращается в удобный прицел. |
| **2** | Кликаете левой кнопкой мыши по карте, расставляя углы будущего участка или дома. | Между точками натягивается яркая контрастная линия, а система на лету считает площадь. |
| **3** | Завершаете контур: нажмите клавишу \`Enter\` или кликните повторно по последней (либо по первой начальной) точке. | Контур замыкается и надёжно сохраняется в проект. |
| **4** | Справа открывается **Паспорт объекта**. | Карта плавно подлетает к вашему участку, чтобы вы могли указать номер, статус («В продаже») и загрузить фото. |

---

## 3. Как выглядит чертёж в процессе

Во время рисования контур специально подсвечивается особым контрастным стилем:

* 🔴 **Яркий красный пунктир:** линии границы отчётливо видны на любой подложке — они не потеряются ни на ярком спутниковом снимке, ни на фоне зелёной травы или асфальта.
* ⚪ **Белые точки узлов:** система подсвечивает первую и последнюю точки, чтобы было легко ориентироваться и замыкать контур.
* 🔍 **Полупрозрачная заливка (10%):** внутренняя часть контура подсвечена очень мягко, поэтому сквозь неё отлично видны ориентиры на местности — деревья, заборы и дороги.

---

## 4. Секрет ровных линий: волшебная клавиша Shift

Чтобы нарисовать идеально ровный прямоугольный дом или прямой забор, не нужно вымерять миллиметры глазомером:

:::keyboard Умное выравнивание углов (Angle Snapping)
Держите нажатой клавишу **\`Shift\`** во время расстановки точек:
* **Для первой линии:** клавиша \`Shift\` сразу привязывает направление строго по сторонам света (Север — Юг — Запад — Восток) или под аккуратным углом 45°.
* **Для следующих стен и ребер:** система смотрит на предыдущую стену и фиксирует курсор под **строгим прямым углом 90°** или 45°.
* **Для 4-й точки (идеальный прямоугольник):** при наличии 3 точек зажатый \`Shift\` автоматически притягивает 4-й угол сразу к обеим смежным сторонам под **90°**, замыкая безупречный прямоугольник без ручной подгонки.
:::

### Быстрые клавиши при черчении и правке

| Клавиша | Режим | Действие |
| :--- | :---: | :--- |
| **Удержание \`Shift\`** | Рисование / Правка | Прямые углы 90° / 45° и автопривязка прямоугольника |
| **\`Ctrl + Z\` / \`Backspace\`** | Рисование | Отменить последнюю поставленную точку |
| **\`Ctrl + Z\` / \`Ctrl + Y\`** | Правка геометрии | Откатить / повторить шаг изменения вершин |
| **\`Delete\` / \`Backspace\`** | Правка геометрии | Удалить выделенную вершину контура |
| **\`Enter\`** | Рисование / Правка | Завершить создание контура / Сохранить изменения |
| **\`Esc\`** | Рисование / Правка | Отмена / Выход из режима без сохранения |

---

## 5. Как изменить границы без потери данных

Если забор перенесли или границы участка уточнили, **удалять объект и создавать его заново не нужно**. Все данные паспорта (фотографии, цена, примечания, контакты) останутся на месте.

:::card icon="edit-2" title="Как скорректировать границы объекта:"
1. Кликните по объекту на карте, чтобы справа открылся его **Паспорт**.
2. В меню действий нажмите кнопку **«Изменить геометрию»** (иконка с карандашом ✏️).
3. На контуре появятся вершины (узловые точки):
   * **Двигайте узлы:** перетаскивайте существующие вершины мышью.
   * **Добавляйте узлы:** делайте **двойной клик** по ребру контура.
   * **Удаляйте лишние узлы:** выделите вершину и нажмите \`Delete\` / \`Backspace\`.
   * **Откат и повтор:** используйте \`Ctrl+Z\` и \`Ctrl+Y\` для отмены шагов.
   * **Сброс с нуля:** кнопка «🗑 С нуля» в верхней панели очищает точки и позволяет обвести объект заново.
4. Нажмите **«Сохранить»** (или клавишу \`Enter\`).

Система обновит только линии на карте, а вся заполненная информация, фотографии и статус сохранятся полностью!
:::

---

## 6. Потренируйтесь прямо здесь

Попробуйте начертить свой первый участок, здание или линию в интерактивной песочнице ниже.

:::card icon="mouse-pointer" title="Как завершить рисование:"
* **Клавиша \`Enter\`:** мгновенно завершает построение и фиксирует объект.
* **Клик по последней или первой точке:** кликните повторно по только что поставленной точке (или по самой первой стартовой точке контура), чтобы завершить чертёж и замкнуть фигуру.
:::

:::tip
**Горячие клавиши во время тренировки:**
* **Зажмите \`Shift\`** — включит режим строгих углов под **90°** и **45°**, а при рисовании 4-й точки участка автоматически рассчитает и привяжет идеальный прямоугольный угол сразу к двум сторонам.
* **Нажмите \`Ctrl+Z\` или \`Backspace\`** — пошагово отменит последнюю ошибочно поставленную точку, позволяя перерисовать отрезок без сброса всего контура.
* **Нажмите \`Esc\`** — сбросит черновик и позволит начать заново с чистого листа.
:::

{{sandbox:drawing-plot}}
`
};
